import {
  AnyAction,
  createSlice,
  PayloadAction,
  Reducer,
  Slice,
} from '@reduxjs/toolkit';

import { getFirmwaresWithPagination } from '../../../../shared/thunks/get-firmwares-with-pagination/get-firmwares-with-pagination.thunk';
import { getFirmwares } from '../../../../shared/thunks/get-firmwares/get-firmwares.thunk';
import { PaginationDefaults } from '../../../../shared/utils/pagination-defaults.util';
import { FiltersPayloadModel } from '../../../shared/models/filters-payload.model';
import { FirmwareFilters } from '../../models/firmware-filters.model';
import { FirmwareHeaderAndPaginationFilters } from '../../models/firmware-header-and-pagination-filters.model';
import { FirmwareStateModel } from '../../models/firmware-state.model';
import { getBrands } from '../../thunk/get-brands.thunk';
import { getEcusByModel } from '../../thunk/get-ecus-by-model.thunk';
import { getFirmwareTotalCount } from '../../thunk/get-firmware-total-count.thunk';
import { getModelsByBrand } from '../../thunk/get-models-by-brand.thunk';
import { FirmwareReducers } from '../../types/firmware-reducers.type';

const firmwareFiltersInitialState: FirmwareHeaderAndPaginationFilters = {
  header: {
    name: '',
    modelIds: [],
    platformIds: [],
    ecuIds: [],
  },
  pagination: {
    totalCount: PaginationDefaults.totalCount,
    pageNumber: PaginationDefaults.pageNumber,
    size: PaginationDefaults.size,
    offset: PaginationDefaults.offset,
  },
};

const initialState: FirmwareStateModel = {
  firmwares: [],
  firmwareFileId: null,
  firmwareFile: null,
  filters: firmwareFiltersInitialState,
  brands: [],
  models: [],
  ecus: [],
  areFirmwareFiltersVisible: false,
};

export const firmwareSlice: Slice<
  FirmwareStateModel,
  FirmwareReducers,
  string
> = createSlice({
  name: 'firmware',
  initialState,
  reducers: {
    resetBrandFilter: state => {
      state.filters.header.modelIds = [];
      state.filters.header.ecuIds = [];
    },
    resetModelFilter(state) {
      state.filters.header.ecuIds = [];
    },
    setFirmwareFile(state, action) {
      state.firmwareFileId = action.payload.id;
      state.firmwareFile = action.payload;
    },
    removeFirmwareFile(state) {
      state.firmwareFileId = null;
      state.firmwareFile = null;
    },
    resetFirmwareFilesState(state) {
      state.firmwareFileId = null;
      state.firmwareFile = null;
      state.ecus = [];
      state.brands = [];
      state.models = [];
    },
    clearFirmwareFilters(state) {
      state.filters = firmwareFiltersInitialState;
    },
    clearFirmwaresState(state) {
      Object.assign(state, initialState);
    },

    filterFirmwaresByName(
      state,
      { payload }: PayloadAction<FiltersPayloadModel<FirmwareFilters>>
    ) {
      state.filters.header = {
        ...state.filters.header,
        name: payload.filterValue,
      };
    },

    filterFirmwaresByModel(state, { payload }: PayloadAction<number[]>) {
      state.filters.header = {
        ...state.filters.header,
        modelIds: payload,
      };
    },

    filterFirmwaresByBrand(
      state,
      { payload }: PayloadAction<number | undefined>
    ) {
      state.filters.header = {
        ...state.filters.header,
        brandId: payload,
      };
    },

    filterFirmwaresByEcus(state, { payload }: PayloadAction<number[]>) {
      state.filters.header = {
        ...state.filters.header,
        ecuIds: payload,
      };
    },

    filterFirmwaresByPlatform(state, { payload }: PayloadAction<number[]>) {
      state.filters.header = {
        ...state.filters.header,
        platformIds: payload,
      };
    },

    addPagination(state, action) {
      state.filters.pagination.size = action.payload.size;
      state.filters.pagination.offset = action.payload.offset;
      state.filters.pagination.pageNumber = action.payload.pageNumber;
    },

    resetPagination(state, action) {
      state.filters.pagination.offset = PaginationDefaults.offset;
      state.filters.pagination.size = action.payload.size;
      state.filters.pagination.pageNumber = PaginationDefaults.pageNumber;
    },
    firmwareToggleFiltersVisibility(state) {
      state.areFirmwareFiltersVisible = !state.areFirmwareFiltersVisible;
    },
  },

  extraReducers: builder => {
    builder
      .addCase(getFirmwares.fulfilled, function (state, action) {
        state.firmwares = action.payload;
      })
      .addCase(getFirmwaresWithPagination.fulfilled, function (state, action) {
        state.firmwares = action.payload;
      })
      .addCase(getBrands.fulfilled, function (state, action) {
        state.brands = action.payload;
      })
      .addCase(getModelsByBrand.fulfilled, function (state, action) {
        state.models = action.payload;
      })
      .addCase(getEcusByModel.fulfilled, function (state, action) {
        state.ecus = action.payload;
      })
      .addCase(getFirmwareTotalCount.fulfilled, function (state, action) {
        state.filters.pagination.totalCount = action.payload;
      });
  },
});

const firmwareSliceReducers: Reducer<FirmwareStateModel, AnyAction> =
  firmwareSlice.reducer;

export const {
  resetFirmwareFilesState,
  clearFirmwareFilters,
  filterFirmwaresByName,
  filterFirmwaresByModel,
  filterFirmwaresByBrand,
  filterFirmwaresByPlatform,
  filterFirmwaresByEcus,
  resetBrandFilter,
  resetModelFilter,
  setFirmwareFile,
  removeFirmwareFile,
  clearFirmwaresState,
  addPagination,
  resetPagination,
  firmwareToggleFiltersVisibility,
} = firmwareSlice.actions;

export default firmwareSliceReducers;
